import { AgGridModule } from '@ag-grid-community/angular';
import { CommonModule } from '@angular/common';
import { Component, Input, inject, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateTimeSettings } from '@cdw-ae/common-data';
import {
  CdwColDef,
  CdwSearchGridModule,
} from '@cdw-ae/common-ui-components-search-grid';
import { DateTimeAngularService } from '@cdw-ae/date-time-angular-service';
import { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { MatchersService } from '@cdw-ae/filter-bar-matcher-configs';
import { FilterBarSelectionMatchersModule } from '@cdw-ae/filter-bar-selection-matchers';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { selectCustomerDateTimeSettings } from '../../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../../common/constants/tailwind-colors';
import { EmptyStateOverlayComponent } from '../../../../../../components/empty-state-overlay/empty-state-overlay.component';
import { GopherBuddySessionsDataService } from './gopher-buddy-sessions-data.service';

/**
 * Gopher Buddy Sessions Component
 *
 * Displays a searchable/filterable grid of Gopher Buddy session data
 * using the legacy API endpoint from DevicesDataService.
 */

@Component({
  selector: 'gopher-buddy-sessions',
  standalone: true,
  imports: [
    CommonModule,
    AgGridModule,
    CdwSearchGridModule,
    EmptyStateOverlayComponent,
    MatProgressSpinnerModule,
    FilterBarSelectionMatchersModule,
  ],
  templateUrl: './gopher-buddy-sessions.component.html',
  styleUrl: './gopher-buddy-sessions.component.scss',
})
export class GopherBuddySessionsComponent implements OnInit, OnDestroy {
  @Input() deviceId: string | null = null;

  private destroy$ = new Subject<void>();
  private readonly store = inject(Store);
  private readonly dateTimeService = inject(DateTimeAngularService);

  // Date/time settings observable
  dateTimeSettings$!: Observable<DateTimeSettings>;
  private currentSettings: DateTimeSettings | null = null;

  // Tailwind colors
  public readonly gray50 = TailwindColors.gray50;
  public readonly gray300 = TailwindColors.gray300;
  public readonly gray400 = TailwindColors.gray400;

  // Search grid configuration
  public itemLabel = 'Session';
  public componentId = 'gopherBuddySessionsGrid';
  public columnDefs: CdwColDef[] = [];
  public filterBarSettings: FilterBarField[] = [];

  // Show empty state overlay for testing
  public showEmptyState = false;

  // Loading state
  public isLoading = false;

  constructor(
    public gopherBuddySessionsDataService: GopherBuddySessionsDataService
  ) {
    // Initialize filter bar settings using MatchersService pattern
    this.filterBarSettings = [
      {
        name: 'user',
        displayName: 'User',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'startTime',
        displayName: 'Session Start',
        fieldMatchers: [MatchersService.Matchers.dateTime],
      },
      {
        name: 'duration',
        displayName: 'Session Duration (min)',
        fieldMatchers: [MatchersService.Matchers.numberMatch],
      },
      {
        name: 'privateIp',
        displayName: 'Session Private IP',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'publicIp',
        displayName: 'Session Public IP',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'crosVersion',
        displayName: 'Chrome OS Version',
        fieldMatchers: [
          { ...MatchersService.Matchers.startsWith, suggestMatcher: true },
          MatchersService.Matchers.fullMatch,
        ],
      },
      {
        name: 'deviceOrgUnitPath',
        displayName: 'Device OU',
        fieldMatchers: [MatchersService.Matchers.ouTreePicker],
      },
    ];

    // Initialize column definitions
    this.columnDefs = [
      {
        headerName: 'User',
        field: 'user',
        sortable: true,
        filter: true,
        minWidth: 220,
      },
      {
        headerName: 'Session Start',
        field: 'startTime',
        sortable: true,
        filter: true,
        minWidth: 180,
        valueFormatter: (params) => {
          if (!params.value) return 'N/A';
          if (!this.currentSettings)
            return new Date(params.value).toLocaleString();
          return this.dateTimeService.format(
            params.value,
            this.currentSettings,
            true
          );
        },
      },
      {
        headerName: 'Session Duration',
        field: 'duration',
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: 'Session Private IP',
        field: 'privateIp',
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: 'Session Public IP',
        field: 'publicIp',
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: 'Chrome OS Version',
        field: 'crosVersion',
        sortable: true,
        filter: true,
        minWidth: 150,
      },
      {
        headerName: 'Device OU Path',
        field: 'deviceOrgUnitPath',
        sortable: true,
        filter: true,
        minWidth: 200,
        flex: 1,
      },
    ];
  }

  ngOnInit(): void {
    this.dateTimeSettings$ = this.store.select(selectCustomerDateTimeSettings);

    // Subscribe to date/time settings changes to update formatting
    this.dateTimeSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe((settings) => {
        this.currentSettings = settings;
      });

    // Subscribe to empty state changes
    this.gopherBuddySessionsDataService.isEmpty$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEmpty) => {
        this.showEmptyState = isEmpty;
      });

    // Subscribe to loading state changes
    this.gopherBuddySessionsDataService.isLoading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isLoading) => {
        this.isLoading = isLoading;
      });

    // Set the device ID in the data service so it can fetch sessions
    if (this.deviceId) {
      this.gopherBuddySessionsDataService.setDeviceId(this.deviceId);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
